import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { API_BASE_URL } from '@/lib/api';

interface ManualVerificationProps {
  initialEmail?: string;
  helperText?: string;
}

export function ManualVerification({ initialEmail = '', helperText }: ManualVerificationProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    setEmail(initialEmail);
  }, [initialEmail]);

  const handleVerify = async () => {
    const normalizedEmail = email.trim();
    const normalizedCode = code.trim();

    if (!normalizedEmail) {
      toast({
        title: 'Email required',
        description: 'Enter the email address you used to sign up.',
        variant: 'destructive',
      });
      return;
    }

    if (!/^\d{6}$/.test(normalizedCode)) {
      toast({
        title: 'Code required',
        description: 'Enter the 6-digit verification code from your email.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail, code: normalizedCode }),
      });

      const data = await response.json();

      if (response.ok) {
        sessionStorage.removeItem('verificationEmail');
        toast({
          title: 'Email verified!',
          description: 'You can now log in to your account.',
        });
        navigate('/auth?tab=login');
      } else {
        toast({
          title: 'Verification failed',
          description: data.message || 'Invalid or expired verification code.',
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to verify email. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      toast({
        title: 'Email required',
        description: 'Enter your email first so I know where to send the new code.',
        variant: 'destructive',
      });
      return;
    }

    setResending(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Could not resend verification email.');
      }

      sessionStorage.setItem('verificationEmail', normalizedEmail);
      toast({
        title: 'Verification email sent',
        description: data.message || 'Check your inbox for a fresh verification link and code.',
      });
    } catch (error) {
      toast({
        title: 'Resend failed',
        description: error instanceof Error ? error.message : 'Could not resend verification email.',
        variant: 'destructive',
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="p-6 border rounded-lg bg-card shadow-sm">
      <h3 className="text-lg font-semibold mb-2">Verify your email</h3>
      <p className="text-sm text-muted-foreground mb-4">
        {helperText || 'Open the verification email we sent you. The fastest path is the button in the email, but you can also enter the 6-digit code here.'}
      </p>
      <div className="space-y-3">
        <Input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />
        <Input
          placeholder="6-digit code"
          inputMode="numeric"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
        />
        <div className="grid gap-2 sm:grid-cols-2">
          <Button onClick={handleVerify} disabled={loading}>
            {loading ? 'Verifying...' : 'Verify Email'}
          </Button>
          <Button type="button" variant="outline" onClick={handleResend} disabled={resending}>
            {resending ? 'Sending...' : 'Send New Code'}
          </Button>
        </div>
      </div>
    </div>
  );
}
